/*
 * j3c-quote-calculator.js — STL quote request flow.
 *
 * Lives in its own module rather than assets/theme.js because it needs `import`: three.js
 * and STLLoader are vendored in assets/j3c-three.js and dynamically imported the first time
 * a customer picks a file, so visitors who never upload never pay for the 1.3MB bundle.
 *
 * Everything it needs is read from data-* attributes on the section root, following the
 * theme's convention (CLAUDE.md §7). No configuration is hardcoded here except the pricing
 * formula itself, which is unchanged from the original calculator.
 *
 * The price this file computes is an ESTIMATE SHOWN TO THE CUSTOMER. It is sent to the
 * quote Worker as a hint and lands on a Shopify draft order marked unverified. It is never
 * charged: a human confirms the price and sends the invoice by hand.
 */

(() => {
  const SELECTOR = '[data-quote-calculator]';

  /** Lazily-resolved vendored three.js bundle, shared by every instance on the page. */
  let threePromise = null;
  const loadThree = (url) => {
    if (!threePromise) threePromise = import(url);
    return threePromise;
  };

  function init(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const q = (selector) => root.querySelector(selector);
    const form = q('[data-quote-form]');
    if (!form) return; // Section is rendering its "setup required" notice.

    const fileInput = q('[data-quote-file]');
    const material = q('[data-material]');
    const color = q('[data-color]');
    const layer = q('[data-layer]');
    const calculate = q('[data-calculate]');
    const submitButton = q('[data-submit-quote]');
    const status = q('[data-form-status]');
    const submitHint = q('[data-submit-hint]');
    const success = q('[data-quote-success]');
    const viewer = q('[data-viewer]');

    const data = root.dataset;
    const strings = {
      analyzing: data.strAnalyzing,
      ready: data.strReady,
      fileError: data.strFileError,
      tooLarge: data.strTooLarge,
      wrongType: data.strWrongType,
      submitting: data.strSubmitting,
      submit: data.strSubmit,
      error: data.strError,
      reference: data.strReference,
    };
    const maxBytes = Number(data.maxBytes) || 0;
    const currency = data.currency || 'EUR';
    // Intl.NumberFormat THROWS on a malformed locale or currency tag. That throw would land
    // in init(), before a single listener is wired, and the whole calculator would sit there
    // inert with the submit button disabled. Degrade to the browser default instead.
    const money = (() => {
      for (const locale of [data.locale, document.documentElement.lang, undefined]) {
        try {
          return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency });
        } catch (error) {
          /* try the next candidate */
        }
      }
      return { format: (value) => `${value.toFixed(2)} ${currency}` };
    })();

    let THREE = null;
    let volumeMm3 = 0;
    let estimate = null;
    let renderer, scene, camera, mesh, frame, resizeObserver;

    const setText = (selector, value) => {
      const el = q(selector);
      if (el) el.textContent = value;
    };

    /** The submit button is only ever disabled for want of an estimate; say so. */
    const syncSubmitState = () => {
      submitButton.disabled = !estimate;
      if (submitHint) submitHint.hidden = Boolean(estimate);
    };

    /** No usable file: drop the estimate and close the breakdown. */
    const clearEstimate = () => {
      estimate = null;
      q('[data-breakdown]').hidden = true;
      syncSubmitState();
    };

    const showStatus = (message, busy) => {
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
      status.classList.toggle('quote-calculator__status--busy', Boolean(busy));
    };

    // ── Contact validation ──────────────────────────────────────────────────────────
    // MIRROR OF app/quote-worker/src/validation.js. Read that file before touching
    // anything below and change the two together — the Worker is the AUTHORITATIVE layer.
    // It re-runs every rule here, so a request that skips this form entirely gets exactly
    // the same answers; this copy exists only so the customer is told what is wrong before
    // a multi-megabyte upload leaves their machine.
    //
    // The rules are a little stricter than RFC 5322 on purpose. Shopify's draftOrderCreate
    // refuses addresses it considers unroutable ("Email contains an invalid domain name")
    // and refuses draft orders whose phone is not a plausible number, and by then the
    // customer has already been told their request was received.
    const LOCAL_ATOM = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+$/;
    const DOMAIN_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
    const DOMAIN_TLD = /^[a-z]{2,63}$/;
    const RESERVED_TLDS = ['localhost', 'local', 'test', 'invalid', 'example', 'internal'];
    const E164 = /^\+[1-9]\d{6,14}$/;
    const PHONE_SEPARATORS = /[\s()./\u2010-\u2015\u2212-]/g;

    const invalid = (code) => ({ ok: false, code });

    const validators = {
      customer_name(raw) {
        const value = String(raw || '').replace(/\s+/g, ' ').trim();
        if (!value) return invalid('name_required');
        if (value.length > 200) return invalid('name_too_long');
        return { ok: true, value };
      },

      customer_email(raw) {
        const value = String(raw || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (!value) return invalid('email_required');
        if (value.length > 254) return invalid('email_too_long');
        if (/[^\x21-\x7e]/.test(value)) return invalid('email_invalid');
        if ((value.match(/@/g) || []).length !== 1) return invalid('email_invalid');

        const parts = value.split('@');
        const local = parts[0];
        const domain = parts[1];

        if (!local || local.length > 64) return invalid('email_invalid');
        if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) {
          return invalid('email_invalid');
        }
        if (!local.split('.').every((atom) => LOCAL_ATOM.test(atom))) return invalid('email_invalid');

        if (!domain || domain.length > 253) return invalid('email_domain_invalid');
        if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) {
          return invalid('email_domain_invalid');
        }

        const labels = domain.split('.');
        if (labels.length < 2) return invalid('email_domain_invalid');
        if (!labels.every((label) => DOMAIN_LABEL.test(label))) return invalid('email_domain_invalid');

        const tld = labels[labels.length - 1];
        if (!DOMAIN_TLD.test(tld) || RESERVED_TLDS.includes(tld)) {
          return invalid('email_domain_invalid');
        }
        return { ok: true, value };
      },

      customer_phone(raw) {
        const trimmed = String(raw || '').trim();
        if (!trimmed) return invalid('phone_required');

        let compact = trimmed.replace(PHONE_SEPARATORS, '');
        if (compact.startsWith('00')) compact = `+${compact.slice(2)}`;
        if (!compact.startsWith('+') || !E164.test(compact)) return invalid('phone_invalid');
        return { ok: true, value: compact };
      },
    };

    const CONTACT_FIELDS = ['customer_name', 'customer_email', 'customer_phone'];
    const ERROR_FIELDS = CONTACT_FIELDS.concat(['rights_confirmed']);

    // Stable error codes from validators above and from the Worker's 422 body, resolved to
    // the localized strings the section put on the root element.
    const errorStrings = {
      summary: data.errSummary,
      name_required: data.errNameRequired,
      name_too_long: data.errNameTooLong,
      email_required: data.errEmailRequired,
      email_invalid: data.errEmailInvalid,
      email_domain_invalid: data.errEmailDomainInvalid,
      email_too_long: data.errEmailTooLong,
      email_rejected: data.errEmailRejected,
      phone_required: data.errPhoneRequired,
      phone_invalid: data.errPhoneInvalid,
      phone_rejected: data.errPhoneRejected,
      rights_required: data.errRightsRequired,
      configuration_invalid: data.errConfigurationInvalid,
    };

    // Field names arrive from the Worker's 422 body as well as from our own validators, so
    // they are constrained before being interpolated into a selector.
    const errorSlot = (field) =>
      (/^[a-z_]+$/.test(field) ? q(`[data-error-for="${field}"]`) : null);
    const controlFor = (field) => form.elements[field] || null;

    function setFieldError(field, code) {
      const slot = errorSlot(field);
      const control = controlFor(field);
      if (slot) {
        slot.textContent = errorStrings[code] || errorStrings.summary || '';
        slot.hidden = false;
      }
      if (control) {
        control.setAttribute('aria-invalid', 'true');
        if (slot && slot.id) control.setAttribute('aria-describedby', slot.id);
      }
    }

    function clearFieldError(field) {
      const slot = errorSlot(field);
      const control = controlFor(field);
      if (slot) {
        slot.textContent = '';
        slot.hidden = true;
      }
      if (control) {
        control.removeAttribute('aria-invalid');
        control.removeAttribute('aria-describedby');
      }
    }

    const clearAllFieldErrors = () => ERROR_FIELDS.forEach(clearFieldError);

    /** Every failure at once, so nobody resubmits three times to find three problems. */
    function validateContact() {
      const errors = {};
      const values = {};
      CONTACT_FIELDS.forEach((field) => {
        const result = validators[field](controlFor(field).value);
        if (result.ok) values[field] = result.value;
        else errors[field] = { code: result.code };
      });
      if (!form.elements.rights_confirmed.checked) {
        errors.rights_confirmed = { code: 'rights_required' };
      }
      return { ok: Object.keys(errors).length === 0, values, errors };
    }

    /**
     * Renders a `{ field: { code } }` map — from validateContact() or from the Worker's 422
     * — next to the fields it names. A field with no error slot of its own (the file, the
     * configuration selects) falls back to the status banner so nothing is silently lost.
     */
    function showFieldErrors(errors) {
      let firstInvalid = null;
      ERROR_FIELDS.forEach((field) => {
        if (!errors[field]) return clearFieldError(field);
        setFieldError(field, errors[field].code);
        if (!firstInvalid) firstInvalid = controlFor(field);
      });

      const unanchored = Object.keys(errors).find((field) => !errorSlot(field));
      showStatus(
        (unanchored && errorStrings[errors[unanchored].code]) || errorStrings.summary || ''
      );

      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    CONTACT_FIELDS.forEach((field) => {
      const control = controlFor(field);
      if (!control) return;
      const isFlagged = () => errorSlot(field) && errorSlot(field).hidden === false;

      // Validate what was actually typed. Tabbing through an untouched field must not
      // light it up red — the submit-time pass catches an empty required field anyway.
      control.addEventListener('blur', () => {
        if (!control.value.trim() && !isFlagged()) return;
        const result = validators[field](control.value);
        if (result.ok) clearFieldError(field);
        else setFieldError(field, result.code);
      });

      // Clear as soon as it is right; never flag mid-word.
      control.addEventListener('input', () => {
        if (isFlagged() && validators[field](control.value).ok) clearFieldError(field);
      });
    });

    form.elements.rights_confirmed.addEventListener('change', () => {
      if (form.elements.rights_confirmed.checked) clearFieldError('rights_confirmed');
    });

    // ── Geometry ────────────────────────────────────────────────────────────────────
    // Signed tetrahedron sum. Unchanged from the original calculator; verified against a
    // 20mm cube (8000 mm³).
    function modelVolume(geometry) {
      const position = geometry.attributes.position;
      const a = new THREE.Vector3();
      const b = new THREE.Vector3();
      const c = new THREE.Vector3();
      let result = 0;
      for (let i = 0; i < position.count; i += 3) {
        a.fromBufferAttribute(position, i);
        b.fromBufferAttribute(position, i + 1);
        c.fromBufferAttribute(position, i + 2);
        result += a.dot(b.clone().cross(c)) / 6;
      }
      return Math.abs(result);
    }

    // ── Preview ─────────────────────────────────────────────────────────────────────
    function showModel(sourceGeometry) {
      if (!renderer) {
        viewer.innerHTML = '';
        scene = new THREE.Scene();
        scene.background = new THREE.Color(0x0f1724);
        camera = new THREE.PerspectiveCamera(45, 1, 0.1, 10000);
        renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        viewer.appendChild(renderer.domElement);
        scene.add(new THREE.AmbientLight(0xffffff, 1.4));
        const light = new THREE.DirectionalLight(0xffffff, 2.2);
        light.position.set(1, 1, 2);
        scene.add(light);

        let dragging = false;
        let x = 0;
        let y = 0;
        renderer.domElement.addEventListener('pointerdown', (event) => {
          dragging = true;
          x = event.clientX;
          y = event.clientY;
          renderer.domElement.setPointerCapture(event.pointerId);
        });
        renderer.domElement.addEventListener('pointermove', (event) => {
          if (!dragging || !mesh) return;
          mesh.rotation.y += (event.clientX - x) * 0.01;
          mesh.rotation.x += (event.clientY - y) * 0.01;
          x = event.clientX;
          y = event.clientY;
        });
        renderer.domElement.addEventListener('pointerup', () => { dragging = false; });
        renderer.domElement.addEventListener('wheel', (event) => {
          event.preventDefault();
          camera.position.z = Math.max(0.1, camera.position.z * (1 + event.deltaY * 0.001));
        }, { passive: false });

        const resize = () => {
          const w = viewer.clientWidth;
          const h = viewer.clientHeight;
          if (!w || !h) return;
          renderer.setSize(w, h, false);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
        };
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(viewer);
        resize();

        const animate = () => {
          renderer.render(scene, camera);
          frame = requestAnimationFrame(animate);
        };
        animate();
      }

      if (mesh) {
        scene.remove(mesh);
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      const geometry = sourceGeometry.clone();
      geometry.computeBoundingBox();
      const center = geometry.boundingBox.getCenter(new THREE.Vector3());
      geometry.translate(-center.x, -center.y, -center.z);
      geometry.computeVertexNormals();
      mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
          color: color.selectedOptions[0].dataset.hex,
          roughness: 0.65,
          metalness: 0.05,
        })
      );
      scene.add(mesh);
      const size = geometry.boundingBox.getSize(new THREE.Vector3());
      const max = Math.max(size.x, size.y, size.z) || 100;
      camera.position.set(0, 0, (max / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)))) * 1.45);
      camera.near = Math.max(0.01, camera.position.z / 1000);
      camera.far = camera.position.z * 20;
      camera.updateProjectionMatrix();
    }

    function teardown() {
      if (frame) cancelAnimationFrame(frame);
      if (resizeObserver) resizeObserver.disconnect();
      if (mesh) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss?.();
      }
      renderer = null;
    }

    // ── File selection ──────────────────────────────────────────────────────────────
    fileInput.addEventListener('change', async () => {
      const file = fileInput.files && fileInput.files[0];
      clearEstimate();
      volumeMm3 = 0;
      q('[data-analysis]').hidden = true;
      calculate.disabled = true;
      if (!file) return;

      if (!/\.stl$/i.test(file.name)) {
        setText('[data-file-status]', strings.wrongType);
        fileInput.value = '';
        return;
      }
      if (maxBytes && file.size > maxBytes) {
        setText('[data-file-status]', strings.tooLarge);
        fileInput.value = '';
        return;
      }

      setText('[data-file-status]', strings.analyzing);
      try {
        if (!THREE) THREE = await loadThree(data.threeUrl);
        const geometry = new THREE.STLLoader().parse(await file.arrayBuffer());
        volumeMm3 = modelVolume(geometry);
        if (!Number.isFinite(volumeMm3) || volumeMm3 <= 0) throw new Error('Invalid STL volume');
        setText('[data-file-status]', strings.ready.replace('%%FILENAME%%', file.name));
        setText('[data-volume]', `${(volumeMm3 / 1000).toFixed(2)} cm³`);
        q('[data-analysis]').hidden = false;
        calculate.disabled = false;
        showModel(geometry);
        renderEstimate();
      } catch (error) {
        volumeMm3 = 0;
        setText('[data-file-status]', strings.fileError);
        q('[data-analysis]').hidden = true;
        clearEstimate();
      }
    });

    const reprice = () => (volumeMm3 ? renderEstimate() : clearEstimate());
    material.addEventListener('change', reprice);
    layer.addEventListener('change', reprice);
    color.addEventListener('change', () => {
      if (mesh) mesh.material.color.set(color.selectedOptions[0].dataset.hex);
      reprice();
    });

    // ── Estimate ────────────────────────────────────────────────────────────────────
    // Formula preserved verbatim from the original section. Every input is merchant-owned:
    // the rates arrive on the section root and the per-material density and cost per gram on
    // the <option> data attributes, all of them rendered from Theme settings → Custom quote
    // workflow. Nothing about the price is hardcoded here.
    function renderEstimate() {
      if (!volumeMm3) return;
      const option = material.selectedOptions[0];
      const volumeCm3 = volumeMm3 / 1000;
      const density = Number(option.dataset.density);
      const costPerGram = Number(option.dataset.cost);
      const materialCost = volumeCm3 * density * costPerGram;
      const speedFactor = Math.max(0.5, Math.min(4, 0.2 / Number(layer.value)));
      // Guarded because a zero or missing throughput would make machineCost infinite.
      const throughput = Number(data.machineThroughput) > 0 ? Number(data.machineThroughput) : 60;
      const machineCost = (volumeCm3 / throughput) * speedFactor * Number(data.machineRate);
      const setupFee = Number(data.setupFee);
      const deliveryFee = Number(data.deliveryFee);
      const workCost = machineCost + setupFee;
      const total = materialCost + workCost + deliveryFee;

      setText('[data-material-cost]', money.format(materialCost));
      setText('[data-work-cost]', money.format(workCost));
      setText('[data-delivery-cost]', money.format(deliveryFee));
      setText('[data-total]', money.format(total));
      q('[data-breakdown]').hidden = false;

      estimate = {
        volume_mm3: volumeMm3,
        material_density: density,
        material_cost_per_gram: costPerGram,
        material_cost_cents: Math.round(materialCost * 100),
        work_cost_cents: Math.round(workCost * 100),
        delivery_cost_cents: Math.round(deliveryFee * 100),
        estimate_cents: Math.round(total * 100),
        estimate_formatted: money.format(total),
      };
      syncSubmitState();
      showStatus('');
    }

    // Recalculating on every change is what makes the estimate live, and it closes a trap:
    // previously, changing the colour after calculating silently disabled submit again.
    calculate.addEventListener('click', renderEstimate);

    // ── Submit ──────────────────────────────────────────────────────────────────────
    // Sends the file and the customer's answers to the App Proxy. On failure nothing is
    // cleared: the customer keeps every field and the file, and can press submit again.
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!estimate || submitButton.disabled) return;

      // The form carries `novalidate`: these messages are ours, rendered next to the field,
      // and they use the same rules the Worker will apply in a moment.
      const contact = validateContact();
      if (!contact.ok) return showFieldErrors(contact.errors);
      clearAllFieldErrors();

      const file = fileInput.files && fileInput.files[0];
      if (!file) return;

      const payload = new FormData();
      payload.append('file', file, file.name);
      // Normalized, not raw: the email lowercased and the phone in E.164, which is what
      // gets stored and what Shopify is given. The Worker normalizes again regardless.
      payload.append('customer_name', contact.values.customer_name);
      payload.append('customer_email', contact.values.customer_email);
      payload.append('customer_phone', contact.values.customer_phone);
      payload.append('customer_message', form.elements.customer_message.value.trim());
      payload.append('rights_confirmed', form.elements.rights_confirmed.checked ? 'true' : 'false');
      payload.append('material', material.value);
      payload.append('color', color.value);
      payload.append('layer_height', layer.value);
      payload.append('currency', currency);
      payload.append('locale', data.locale || '');
      Object.entries(estimate).forEach(([key, value]) => payload.append(key, String(value)));

      submitButton.disabled = true;
      submitButton.textContent = strings.submitting;
      showStatus(strings.submitting, true);

      try {
        const response = await fetch(data.endpoint, {
          method: 'POST',
          body: payload,
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        const body = await response.json().catch(() => null);

        // The authoritative validation layer disagreed with ours, or Shopify refused the
        // customer's contact details. Either way it is fixable here: point at the field
        // rather than showing the generic "could not send" message.
        if (response.status === 422 && body && body.errors) {
          showFieldErrors(body.errors);
          syncSubmitState();
          submitButton.textContent = strings.submit;
          return;
        }

        if (!response.ok || !body || body.ok !== true) {
          // A non-JSON body almost always means the request never reached the Worker —
          // Shopify answered it itself (no App Proxy registered, or a password redirect).
          const detail = body
            ? body.message
            : `non-JSON response (${response.headers.get('content-type') || 'unknown type'})`;
          throw new Error(`Quote request failed: HTTP ${response.status} — ${detail}`);
        }

        form.hidden = true;
        success.hidden = false;
        if (body.reference && strings.reference) {
          const reference = q('[data-success-reference]');
          reference.textContent = strings.reference.replace('%%REFERENCE%%', body.reference);
          reference.hidden = false;
        }
        success.focus();
        success.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        // The customer gets a generic message on purpose — never leak endpoint detail into
        // the page. The real cause goes to the console, because the two failures that look
        // identical from the outside (proxy not registered vs. Worker erroring) are only
        // distinguishable by the status code.
        console.error('[j3c-quote] submission failed:', error);
        showStatus(strings.error);
        syncSubmitState();
        submitButton.textContent = strings.submit;
      }
    });

    syncSubmitState();

    // The theme editor tears sections down and rebuilds them; release the WebGL context.
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail.sectionId !== data.sectionId) return;
      teardown();
    });
  }

  const initAll = (scope) => (scope || document).querySelectorAll(SELECTOR).forEach(init);

  initAll();
  // Theme-editor section reloads deliver fresh DOM that has never been wired up.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
